import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================
// PERFORMANCE OPTIMIZATION SYSTEM
// ============================================

export type QualityPreset = 'low' | 'medium' | 'high' | 'ultra' | 'auto';

export interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  memoryUsage: number; // MB
  drawCalls: number;
  triangles: number;
  textureMemory: number; // MB
  gcTime: number; // ms
}

export interface QualitySettings {
  // Rendering
  resolution: number; // 0.5 a 2.0 (multiplicador)
  antiAliasing: 'none' | 'fxaa' | 'msaa2x' | 'msaa4x';
  shadows: 'none' | 'low' | 'medium' | 'high';
  shadowMapSize: 512 | 1024 | 2048 | 4096;
  
  // Effects
  particles: 'none' | 'low' | 'medium' | 'high';
  maxParticles: number;
  postProcessing: boolean;
  bloom: boolean;
  motionBlur: boolean;
  
  // Textures
  textureQuality: 'low' | 'medium' | 'high';
  anisotropicFiltering: 0 | 2 | 4 | 8 | 16;
  
  // LOD
  lodBias: number; // 0 a 2
  maxLodDistance: number;
  
  // Physics/Gameplay
  maxEnemies: number;
  maxBullets: number;
  physicsRate: 30 | 60 | 120;
  
  // Audio
  audioQuality: 'low' | 'medium' | 'high';
  maxAudioChannels: 8 | 16 | 32 | 64;
}

export interface PerformanceProfile {
  id: string;
  name: string;
  description: string;
  settings: QualitySettings;
  targetFps: number;
}

// ============================================
// QUALITY PRESETS
// ============================================

export const QUALITY_PRESETS: Record<Exclude<QualityPreset, 'auto'>, QualitySettings> = {
  low: {
    resolution: 0.5,
    antiAliasing: 'none',
    shadows: 'none',
    shadowMapSize: 512,
    particles: 'low',
    maxParticles: 50,
    postProcessing: false,
    bloom: false,
    motionBlur: false,
    textureQuality: 'low',
    anisotropicFiltering: 0,
    lodBias: 2,
    maxLodDistance: 50,
    maxEnemies: 10,
    maxBullets: 20,
    physicsRate: 30,
    audioQuality: 'low',
    maxAudioChannels: 8,
  },
  medium: {
    resolution: 0.75,
    antiAliasing: 'fxaa',
    shadows: 'low',
    shadowMapSize: 1024,
    particles: 'medium',
    maxParticles: 150,
    postProcessing: true,
    bloom: false,
    motionBlur: false,
    textureQuality: 'medium',
    anisotropicFiltering: 4,
    lodBias: 1,
    maxLodDistance: 100,
    maxEnemies: 20,
    maxBullets: 50,
    physicsRate: 60,
    audioQuality: 'medium',
    maxAudioChannels: 16,
  },
  high: {
    resolution: 1.0,
    antiAliasing: 'msaa2x',
    shadows: 'medium',
    shadowMapSize: 2048,
    particles: 'high',
    maxParticles: 300,
    postProcessing: true,
    bloom: true,
    motionBlur: false,
    textureQuality: 'high',
    anisotropicFiltering: 8,
    lodBias: 0.5,
    maxLodDistance: 200,
    maxEnemies: 30,
    maxBullets: 100,
    physicsRate: 60,
    audioQuality: 'high',
    maxAudioChannels: 32,
  },
  ultra: {
    resolution: 1.5,
    antiAliasing: 'msaa4x',
    shadows: 'high',
    shadowMapSize: 4096,
    particles: 'high',
    maxParticles: 500,
    postProcessing: true,
    bloom: true,
    motionBlur: true,
    textureQuality: 'high',
    anisotropicFiltering: 16,
    lodBias: 0,
    maxLodDistance: 500,
    maxEnemies: 50,
    maxBullets: 200,
    physicsRate: 120,
    audioQuality: 'high',
    maxAudioChannels: 64,
  },
};

// ============================================
// DEVICE DETECTION
// ============================================

export interface DeviceCapabilities {
  isMobile: boolean;
  isLowEnd: boolean;
  gpu: string;
  cores: number;
  memory: number; // GB (estimado)
  touchscreen: boolean;
  webgl2: boolean;
  webgpu: boolean;
  maxTextureSize: number;
  supportsFloat: boolean;
}

const detectDeviceCapabilities = (): DeviceCapabilities => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  
  const cores = navigator.hardwareConcurrency || 4;
  
  // Detect WebGPU support
  const webgpu = typeof navigator !== 'undefined' && 'gpu' in navigator;
  
  // @ts-expect-error - deviceMemory não está no tipo padrão
  const memory = navigator.deviceMemory || 4;
  const touchscreen = 'ontouchstart' in window;
  
  // Detectar GPU via WebGL
  let gpu = 'Unknown';
  let webgl2 = false;
  let maxTextureSize = 2048;
  let supportsFloat = false;
  
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    
    if (gl) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      webgl2 = !!(window as any).WebGL2RenderingContext && gl instanceof (window as any).WebGL2RenderingContext;
      
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        gpu = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'Unknown';
      }
      
      maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
      supportsFloat = !!gl.getExtension('OES_texture_float');
    }
  } catch {
    // Ignore
  }
  
  // Detectar dispositivos low-end
  const isLowEnd = (
    cores <= 2 ||
    memory <= 2 ||
    (isMobile && (cores <= 4 || memory <= 4)) ||
    gpu.toLowerCase().includes('intel') ||
    gpu.toLowerCase().includes('mali-4') ||
    gpu.toLowerCase().includes('adreno 3')
  );
  
  return {
    isMobile,
    isLowEnd,
    gpu,
    cores,
    memory,
    touchscreen,
    webgl2,
    webgpu,
    maxTextureSize,
    supportsFloat,
  };
};

// ============================================
// STATE INTERFACE
// ============================================

interface PerformanceState {
  // Device info
  device: DeviceCapabilities;
  
  // Current settings
  preset: QualityPreset;
  settings: QualitySettings;
  customSettings: Partial<QualitySettings>;
  
  // Metrics
  currentMetrics: PerformanceMetrics;
  metricsHistory: PerformanceMetrics[];
  averageFps: number;
  minFps: number;
  
  // Auto quality
  autoQualityEnabled: boolean;
  targetFps: number;
  fpsThresholdLow: number;
  fpsThresholdHigh: number;
  lastAutoAdjust: number;
  
  // Memory
  memoryWarning: boolean;
  memoryLimit: number; // MB
  
  // Debug
  showDebugOverlay: boolean;
  showFpsCounter: boolean;
  logPerformance: boolean;
  
  // Actions
  setPreset: (preset: QualityPreset) => void;
  setSetting: <K extends keyof QualitySettings>(key: K, value: QualitySettings[K]) => void;
  resetToDefaults: () => void;
  applyRecommendedSettings: () => void;
  
  // Metrics
  updateMetrics: (metrics: Partial<PerformanceMetrics>) => void;
  getAverageMetrics: () => PerformanceMetrics;
  clearMetricsHistory: () => void;
  
  // Auto quality
  enableAutoQuality: (enabled: boolean) => void;
  setTargetFps: (fps: number) => void;
  checkAutoQuality: () => void;
  
  // Memory
  checkMemory: () => void;
  freeMemory: () => void;
  
  // Debug
  toggleDebugOverlay: () => void;
  toggleFpsCounter: () => void;
  
  // Helpers
  getEffectiveSetting: <K extends keyof QualitySettings>(key: K) => QualitySettings[K];
  canUseHighQuality: () => boolean;
  getRecommendedPreset: () => Exclude<QualityPreset, 'auto'>;
  
  // Init
  initialize: () => void;
}

// ============================================
// STORE
// ============================================

export const usePerformance = create<PerformanceState>()(
  persist(
    (set, get) => ({
      // Initial state
      device: detectDeviceCapabilities(),
      
      preset: 'auto',
      settings: QUALITY_PRESETS.medium,
      customSettings: {},
      
      currentMetrics: {
        fps: 60,
        frameTime: 16.67,
        memoryUsage: 0,
        drawCalls: 0,
        triangles: 0,
        textureMemory: 0,
        gcTime: 0,
      },
      metricsHistory: [],
      averageFps: 60,
      minFps: 60,
      
      autoQualityEnabled: true,
      targetFps: 60,
      fpsThresholdLow: 30,
      fpsThresholdHigh: 55,
      lastAutoAdjust: 0,
      
      memoryWarning: false,
      memoryLimit: 512, // MB
      
      showDebugOverlay: false,
      showFpsCounter: false,
      logPerformance: false,
      
      // ============================================
      // PRESET & SETTINGS
      // ============================================
      
      setPreset: (preset) => {
        if (preset === 'auto') {
          set({ 
            preset,
            autoQualityEnabled: true,
          });
          get().checkAutoQuality();
        } else {
          set({
            preset,
            settings: { ...QUALITY_PRESETS[preset], ...get().customSettings },
            autoQualityEnabled: false,
          });
        }
      },
      
      setSetting: (key, value) => {
        set((state) => ({
          customSettings: { ...state.customSettings, [key]: value },
          settings: { ...state.settings, [key]: value },
          preset: 'auto', // Muda para custom
        }));
      },
      
      resetToDefaults: () => {
        const recommended = get().getRecommendedPreset();
        set({
          preset: recommended,
          settings: QUALITY_PRESETS[recommended],
          customSettings: {},
          autoQualityEnabled: true,
        });
      },
      
      applyRecommendedSettings: () => {
        const recommended = get().getRecommendedPreset();
        set({
          preset: recommended,
          settings: QUALITY_PRESETS[recommended],
        });
      },
      
      // ============================================
      // METRICS
      // ============================================
      
      updateMetrics: (metrics) => {
        set((state) => {
          const newMetrics = { ...state.currentMetrics, ...metrics };
          const history = [...state.metricsHistory, newMetrics].slice(-60); // Últimos 60 frames
          
          const fpsValues = history.map(m => m.fps);
          const averageFps = fpsValues.reduce((a, b) => a + b, 0) / fpsValues.length;
          const minFps = Math.min(...fpsValues);
          
          return {
            currentMetrics: newMetrics,
            metricsHistory: history,
            averageFps: Math.round(averageFps),
            minFps,
          };
        });
        
        // Check auto quality periodically
        if (get().autoQualityEnabled) {
          get().checkAutoQuality();
        }
        
        // Check memory
        get().checkMemory();
      },
      
      getAverageMetrics: () => {
        const history = get().metricsHistory;
        if (history.length === 0) return get().currentMetrics;
        
        const sum = history.reduce(
          (acc, m) => ({
            fps: acc.fps + m.fps,
            frameTime: acc.frameTime + m.frameTime,
            memoryUsage: acc.memoryUsage + m.memoryUsage,
            drawCalls: acc.drawCalls + m.drawCalls,
            triangles: acc.triangles + m.triangles,
            textureMemory: acc.textureMemory + m.textureMemory,
            gcTime: acc.gcTime + m.gcTime,
          }),
          { fps: 0, frameTime: 0, memoryUsage: 0, drawCalls: 0, triangles: 0, textureMemory: 0, gcTime: 0 }
        );
        
        const count = history.length;
        return {
          fps: Math.round(sum.fps / count),
          frameTime: sum.frameTime / count,
          memoryUsage: sum.memoryUsage / count,
          drawCalls: Math.round(sum.drawCalls / count),
          triangles: Math.round(sum.triangles / count),
          textureMemory: sum.textureMemory / count,
          gcTime: sum.gcTime / count,
        };
      },
      
      clearMetricsHistory: () => {
        set({ metricsHistory: [], averageFps: 60, minFps: 60 });
      },
      
      // ============================================
      // AUTO QUALITY
      // ============================================
      
      enableAutoQuality: (enabled) => {
        set({ autoQualityEnabled: enabled });
        if (enabled) {
          get().checkAutoQuality();
        }
      },
      
      setTargetFps: (fps) => {
        set({
          targetFps: fps,
          fpsThresholdLow: Math.floor(fps * 0.5),
          fpsThresholdHigh: Math.floor(fps * 0.9),
        });
      },
      
      checkAutoQuality: () => {
        const state = get();
        const now = Date.now();
        
        // Não ajustar muito frequentemente (mínimo 5 segundos)
        if (now - state.lastAutoAdjust < 5000) return;
        
        // Precisa de histórico suficiente
        if (state.metricsHistory.length < 30) return;
        
        const avgFps = state.averageFps;
        const presets: Exclude<QualityPreset, 'auto'>[] = ['low', 'medium', 'high', 'ultra'];
        const currentIndex = presets.indexOf(state.preset === 'auto' ? 'medium' : state.preset as Exclude<QualityPreset, 'auto'>);
        
        let newPreset: Exclude<QualityPreset, 'auto'> | null = null;
        
        // FPS muito baixo - diminuir qualidade
        if (avgFps < state.fpsThresholdLow && currentIndex > 0) {
          newPreset = presets[currentIndex - 1];
        }
        // FPS alto e estável - aumentar qualidade
        else if (avgFps > state.fpsThresholdHigh && state.minFps > state.fpsThresholdLow && currentIndex < presets.length - 1) {
          // Só aumentar se dispositivo suportar
          const device = state.device;
          if (!device.isLowEnd && (!device.isMobile || currentIndex < 2)) {
            newPreset = presets[currentIndex + 1];
          }
        }
        
        if (newPreset) {
          set({
            settings: { ...QUALITY_PRESETS[newPreset], ...state.customSettings },
            lastAutoAdjust: now,
          });
          
          if (state.logPerformance) {
            console.log(`[Performance] Auto-adjusted quality to ${newPreset} (FPS: ${avgFps})`);
          }
        }
      },
      
      // ============================================
      // MEMORY
      // ============================================
      
      checkMemory: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const perf = typeof performance !== 'undefined' ? performance : null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const memory = perf ? (perf as any).memory : null;
        if (!memory) return;
        
        const usedMB = memory.usedJSHeapSize / (1024 * 1024);
        const limitMB = memory.jsHeapSizeLimit / (1024 * 1024);
        
        set((state) => ({
          currentMetrics: {
            ...state.currentMetrics,
            memoryUsage: usedMB,
          },
          memoryWarning: usedMB > state.memoryLimit || usedMB > limitMB * 0.8,
        }));
        
        // Auto-reduce quality on memory warning
        if (get().memoryWarning && get().autoQualityEnabled) {
          const state = get();
          const presets: Exclude<QualityPreset, 'auto'>[] = ['low', 'medium', 'high', 'ultra'];
          const currentIndex = presets.indexOf(state.preset === 'auto' ? 'medium' : state.preset as Exclude<QualityPreset, 'auto'>);
          
          if (currentIndex > 0) {
            const newPreset = presets[currentIndex - 1];
            set({
              settings: { ...QUALITY_PRESETS[newPreset], ...state.customSettings },
            });
          }
        }
      },
      
      freeMemory: () => {
        // Limpar caches
        set({ metricsHistory: [] });
        
        // Sugerir GC (não garante execução)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).gc) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).gc();
        }
        
        // Notificar outros sistemas para liberar memória
        if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
          window.dispatchEvent(new CustomEvent('rocketanks:free-memory'));
        }
      },
      
      // ============================================
      // DEBUG
      // ============================================
      
      toggleDebugOverlay: () => {
        set((state) => ({ showDebugOverlay: !state.showDebugOverlay }));
      },
      
      toggleFpsCounter: () => {
        set((state) => ({ showFpsCounter: !state.showFpsCounter }));
      },
      
      // ============================================
      // HELPERS
      // ============================================
      
      getEffectiveSetting: (key) => {
        const state = get();
        if (key in state.customSettings) {
          return state.customSettings[key] as QualitySettings[typeof key];
        }
        return state.settings[key];
      },
      
      canUseHighQuality: () => {
        const device = get().device;
        return !device.isLowEnd && device.webgl2 && device.memory >= 4;
      },
      
      getRecommendedPreset: () => {
        const device = get().device;
        
        if (device.isLowEnd) return 'low';
        if (device.isMobile) return 'medium';
        if (device.memory >= 8 && device.cores >= 8) return 'ultra';
        if (device.memory >= 4) return 'high';
        
        return 'medium';
      },
      
      // ============================================
      // INIT
      // ============================================
      
      initialize: () => {
        const device = detectDeviceCapabilities();
        const recommended = get().getRecommendedPreset();
        
        set({
          device,
          preset: 'auto',
          settings: QUALITY_PRESETS[recommended],
          autoQualityEnabled: true,
        });
        
        console.log('[Performance] Initialized with device:', device);
        console.log('[Performance] Recommended preset:', recommended);
      },
    }),
    {
      name: 'rocketanks-performance',
      partialize: (state) => ({
        preset: state.preset,
        customSettings: state.customSettings,
        autoQualityEnabled: state.autoQualityEnabled,
        targetFps: state.targetFps,
        showFpsCounter: state.showFpsCounter,
      }),
    }
  )
);

// ============================================
// HOOKS
// ============================================

export function useQualitySetting<K extends keyof QualitySettings>(key: K): QualitySettings[K] {
  return usePerformance((state) => state.getEffectiveSetting(key));
}

export function useFps() {
  return usePerformance((state) => ({
    current: state.currentMetrics.fps,
    average: state.averageFps,
    min: state.minFps,
  }));
}

export function useMemoryStatus() {
  return usePerformance((state) => ({
    usage: state.currentMetrics.memoryUsage,
    warning: state.memoryWarning,
    limit: state.memoryLimit,
  }));
}

export function useDeviceCapabilities() {
  return usePerformance((state) => state.device);
}

// ============================================
// FRAME RATE LIMITER
// ============================================

export class FrameRateLimiter {
  private targetFps: number;
  private frameInterval: number;
  private lastFrameTime: number = 0;
  
  constructor(targetFps = 60) {
    this.targetFps = targetFps;
    this.frameInterval = 1000 / targetFps;
  }
  
  setTargetFps(fps: number) {
    this.targetFps = fps;
    this.frameInterval = 1000 / fps;
  }
  
  shouldRender(currentTime: number): boolean {
    const elapsed = currentTime - this.lastFrameTime;
    
    if (elapsed >= this.frameInterval) {
      this.lastFrameTime = currentTime - (elapsed % this.frameInterval);
      return true;
    }
    
    return false;
  }
}

// ============================================
// OBJECT POOL
// ============================================

export class ObjectPool<T> {
  private pool: T[] = [];
  private createFn: () => T;
  private resetFn: (obj: T) => void;
  private maxSize: number;
  
  constructor(
    createFn: () => T,
    resetFn: (obj: T) => void,
    initialSize = 10,
    maxSize = 100
  ) {
    this.createFn = createFn;
    this.resetFn = resetFn;
    this.maxSize = maxSize;
    
    // Pre-populate pool
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(createFn());
    }
  }
  
  acquire(): T {
    if (this.pool.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.pool.pop()!;
    }
    return this.createFn();
  }
  
  release(obj: T) {
    if (this.pool.length < this.maxSize) {
      this.resetFn(obj);
      this.pool.push(obj);
    }
  }
  
  clear() {
    this.pool = [];
  }
  
  get size(): number {
    return this.pool.length;
  }
}

// ============================================
// INITIALIZATION
// ============================================

if (typeof window !== 'undefined' && typeof performance !== 'undefined') {
  // Inicializar ao carregar
  usePerformance.getState().initialize();
  
  // Monitorar FPS
  let lastTime = performance.now();
  let frameCount = 0;
  
  const measureFps = () => {
    frameCount++;
    const now = performance.now();
    const elapsed = now - lastTime;
    
    if (elapsed >= 1000) {
      const fps = Math.round((frameCount * 1000) / elapsed);
      usePerformance.getState().updateMetrics({ 
        fps,
        frameTime: elapsed / frameCount,
      });
      
      frameCount = 0;
      lastTime = now;
    }
    
    requestAnimationFrame(measureFps);
  };
  
  requestAnimationFrame(measureFps);
}
